import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiRequestError, api } from '../lib/api.js';
import { compressImage } from '../lib/compressImage.js';
import { captureFrame, useCamera } from '../lib/scanner/useCamera.js';
import { Alert, Button, Spinner, cx } from './ui.js';
import { IconCamera } from './icons.js';

/**
 * Foto de como o pacote chegou.
 *
 * NAO e a camera que ja existia na tela: aquela faz OCR da etiqueta e joga o
 * quadro fora — ela le o destino, nao guarda o pacote. Esta responde a outra
 * pergunta, a que aparece uma semana depois: "a caixa ja chegou amassada?".
 * Sem ela, avaria e a palavra do porteiro contra a do morador.
 *
 * Aparece DEPOIS do registro, e nao junto. A encomenda so tem id quando ja foi
 * gravada, e prender a criacao ao upload faria a tela falhar inteira quando a
 * foto falhasse — trocando o registro, que e obrigatorio, pela foto, que e
 * opcional. E por isso tambem que nada aqui bloqueia: o porteiro pode ignorar
 * este bloco e seguir para a proxima caixa.
 */

interface PackagePhotoProps {
  /** `public_id` da encomenda ja registrada. */
  packageId: string;
  code: string;
}

export function PackagePhoto({ packageId, code }: PackagePhotoProps) {
  const camera = useCamera();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const live = camera.status === 'live';
  const { start, stop } = camera;

  useEffect(() => {
    if (open) start();
    else stop();
  }, [open, start, stop]);

  // Encomenda nova zera o bloco: sem isto, a foto da caixa anterior continuaria
  // na tela como se fosse desta.
  useEffect(() => {
    setOpen(false);
    setPreview(null);
    setError(null);
  }, [packageId]);

  const upload = useMutation({
    mutationFn: (photo: string) => api.post<{ photoAt: string }>(`/packages/${packageId}/foto`, { photo }),
    onSuccess: () => {
      setError(null);
      setOpen(false);
    },
    onError: (err) => {
      setPreview(null);
      setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel enviar a foto.');
    },
  });

  if (upload.isSuccess) {
    return (
      <p className="mt-3 text-sm text-accent">Foto da encomenda #{code} anexada.</p>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-line bg-surface-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold">Fotografar o pacote</p>
          <p className="mt-0.5 text-sm text-muted">
            Opcional. Vale a pena quando a caixa chega amassada, molhada ou aberta.
          </p>
        </div>
        <Button
          variant={open ? 'ghost' : 'quiet'}
          className="py-2"
          onClick={() => {
            setOpen((value) => !value);
            setError(null);
          }}
        >
          {open ? 'Cancelar' : 'Abrir camera'}
        </Button>
      </div>

      {error && (
        <div className="mt-3">
          <Alert tone="warn">{error}</Alert>
        </div>
      )}

      {open && (
        <div className="mt-3">
          <div className="relative aspect-video overflow-hidden rounded-xl border border-line bg-ink">
            <video
              ref={camera.videoRef}
              playsInline
              muted
              className={cx('size-full object-cover transition-opacity', live ? 'opacity-100' : 'opacity-0')}
            />

            {preview && (
              <img
                src={preview}
                alt="Previa da foto da encomenda"
                className="absolute inset-0 size-full object-cover"
              />
            )}

            {!live && !preview && (
              <div className="absolute inset-0 grid place-items-center p-4 text-center">
                {camera.status === 'starting' ? (
                  <span className="flex items-center gap-2 text-sm text-muted">
                    <Spinner /> Abrindo a camera...
                  </span>
                ) : (
                  <div>
                    <IconCamera className="mx-auto size-7 text-faint" />
                    <p className="mt-2 text-sm text-danger">{camera.message ?? 'Camera indisponivel.'}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-3">
            {preview ? (
              <>
                <Button
                  loading={upload.isPending}
                  onClick={() => {
                    upload.mutate(preview);
                  }}
                >
                  Usar esta foto
                </Button>
                <Button variant="quiet" onClick={() => setPreview(null)} disabled={upload.isPending}>
                  Tirar outra
                </Button>
              </>
            ) : (
              <Button
                disabled={!live}
                onClick={() => {
                  const video = camera.videoRef.current;
                  if (!video) return;
                  const frame = captureFrame(video);
                  // Comprimido ANTES de virar estado: a data URL crua de um
                  // quadro 1080p tem varios MB e o backend recusa em 1mb.
                  const compressed = frame ? compressImage(frame) : null;
                  if (!compressed) {
                    setError('Nao foi possivel preparar a foto neste dispositivo.');
                    return;
                  }
                  setPreview(compressed.dataUrl);
                }}
              >
                Tirar foto
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
